import { useState, useRef, useEffect } from 'react';
import { 
  Upload, 
  Download, 
  FileText, 
  AlertCircle, 
  CheckCircle, 
  Eye, 
  Loader,
  FileUp,
  Trash2,
  RefreshCw,
  ArrowRight,
  Check,
  ChevronRight,
  Home,
  Sparkles,
  Zap
} from 'lucide-react';
import { Device } from '../lib/supabase';
import { useAuditLog } from '../hooks/useAuditLog';
import { useDevices } from '../hooks/useDevices';

export function BulkImportPage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { logAction } = useAuditLog();
  const { addDevice } = useDevices();
  
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState('');
  const [previewData, setPreviewData] = useState<Omit<Device, 'id' | 'created_at' | 'updated_at'>[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ success: boolean; message: string; count?: number } | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [isAnimating, setIsAnimating] = useState(false);

  // Determine current step based on state with animations
  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 500);

    if (importResult?.success && importResult?.count !== undefined) {
      // Only advance to step 4 if devices were actually imported (count is set)
      setCurrentStep(4);
      setCompletedSteps(new Set([1, 2, 3, 4]));
    } else if (previewData.length > 0 && errors.length === 0) {
      setCurrentStep(3);
      setCompletedSteps(prev => new Set([...prev, 1, 2]));
    } else if (csvFile) {
      setCurrentStep(2);
      setCompletedSteps(prev => new Set([...prev, 1]));
    } else if (completedSteps.has(1)) {
      // If step 1 is completed but no file uploaded yet, stay on step 2
      setCurrentStep(2);
    } else {
      setCurrentStep(1);
    }

    return () => clearTimeout(timer);
  }, [csvFile, previewData, importResult, errors, completedSteps]);

  // CSV template for download
  const csvTemplate = `serial_number,device_name,owner_name,is_locked,note
ABC123,John's Laptop,John Doe,false,Primary work laptop
DEF456,Jane's Desktop,Jane Smith,true,Desktop computer - locked for maintenance
GHI789,Meeting Room Tablet,IT Department,false,Conference room device
JKL012,Marketing Workstation,Sarah Wilson,false,Design and marketing workstation
MNO345,Server Room Terminal,IT Admin,true,Critical infrastructure access terminal`;

  const downloadTemplate = () => {
    const blob = new Blob([csvTemplate], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'payinthi_device_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    // Update completed steps and advance to step 2
    setCompletedSteps(prev => new Set([...prev, 1]));
    setCurrentStep(2);
    
    // Log template download
    logAction('system_status_check', 'device_management', {
      details: { template_name: 'payinthi_device_import_template.csv', action: 'download_template' }
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setImportResult({
        success: false,
        message: 'Please select a valid CSV file.'
      });
      return;
    }

    setCsvFile(file);
    setImportResult(null);
    setErrors([]);

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setCsvData(content);
      
      // Auto-validate and parse the data after upload
      setTimeout(() => {
        const validationErrors = validateCsvData(content);
        setErrors(validationErrors);

        if (validationErrors.length === 0) {
          try {
            const devices = parseCsv(content);
            setPreviewData(devices);
            // Don't auto-import, just prepare for preview step
          } catch (error) {
            setImportResult({
              success: false,
              message: 'Failed to parse CSV data. Please check the format.'
            });
          }
        }
      }, 100);
    };
    reader.readAsText(file);
  };

  const validateCsvData = (data: string): string[] => {
    const validationErrors: string[] = [];
    const lines = data.trim().split('\n');

    if (lines.length < 2) {
      validationErrors.push('CSV file must contain at least a header row and one data row');
      return validationErrors;
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const requiredHeaders = ['serial_number', 'device_name', 'owner_name'];
    const optionalHeaders = ['is_locked', 'note', 'unlock_code'];
    
    // Check for required headers
    for (const required of requiredHeaders) {
      if (!headers.includes(required)) {
        validationErrors.push(`Missing required column: ${required}`);
      }
    }

    // Check for unknown headers
    const allValidHeaders = [...requiredHeaders, ...optionalHeaders];
    const unknownHeaders = headers.filter(h => h && !allValidHeaders.includes(h));
    if (unknownHeaders.length > 0) {
      validationErrors.push(`Unknown columns: ${unknownHeaders.join(', ')}`);
    }

    return validationErrors;
  };

  const parseCsv = (csv: string): Omit<Device, 'id' | 'created_at' | 'updated_at'>[] => {
    const lines = csv.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const devices: Omit<Device, 'id' | 'created_at' | 'updated_at'>[] = [];

    // Generate secure unlock code function for CSV import
    const generateSecureUnlockCode = () => {
      const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      const lowercase = 'abcdefghijklmnopqrstuvwxyz';
      const numbers = '0123456789';
      const symbols = '!@#$%&*';
      const allChars = uppercase + lowercase + numbers + symbols;
      
      let result = '';
      
      // Ensure at least one character from each category
      result += uppercase[Math.floor(Math.random() * uppercase.length)];
      result += lowercase[Math.floor(Math.random() * lowercase.length)];
      result += numbers[Math.floor(Math.random() * numbers.length)];
      result += symbols[Math.floor(Math.random() * symbols.length)];
      
      // Fill the remaining 8 positions with random characters
      for (let i = 4; i < 12; i++) {
        result += allChars[Math.floor(Math.random() * allChars.length)];
      }
      
      // Shuffle the result to avoid predictable patterns
      return result.split('').sort(() => Math.random() - 0.5).join('');
    };

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values.length !== headers.length) continue;

      const device: any = {
        serial_number: '',
        device_name: '',
        owner_name: '',
        unlock_code: '',
        is_locked: false,
        note: ''
      };

      headers.forEach((header, index) => {
        const value = values[index];
        if (header === 'is_locked') {
          device[header] = value.toLowerCase() === 'true';
        } else if (header !== 'unlock_code') { // Ignore unlock_code from CSV if present
          device[header] = value;
        }
      });

      // Always generate a new secure unlock code for each device
      device.unlock_code = generateSecureUnlockCode();

      if (device.serial_number) {
        devices.push(device);
      }
    }

    return devices;
  };

  const handlePreview = () => {
    if (!csvData.trim()) {
      setImportResult({
        success: false,
        message: 'Please upload a CSV file first.'
      });
      return;
    }

    const validationErrors = validateCsvData(csvData);
    setErrors(validationErrors);

    if (validationErrors.length > 0) {
      setImportResult({
        success: false,
        message: 'Please fix the validation errors before previewing.'
      });
      return;
    }

    try {
      const devices = parseCsv(csvData);
      setPreviewData(devices);
      // Don't set importResult here as it's just a preview, not an actual import
    } catch (error) {
      setImportResult({
        success: false,
        message: 'Failed to parse CSV data. Please check the format.'
      });
    }
  };

  const handleImport = async () => {
    if (previewData.length === 0) {
      setImportResult({
        success: false,
        message: 'No valid devices to import. Please preview your data first.'
      });
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      let successCount = 0;
      const results = [];

      // Import devices one by one for better progress tracking
      for (const device of previewData) {
        try {
          const result = await addDevice(device);
          if (result.success) {
            successCount++;
          }
          results.push(result);
        } catch (error) {
          results.push({ success: false, error: 'Failed to import device' });
        }
      }

      // Log the bulk import action
      await logAction(
        'bulk_import_devices',
        'device_management',
        {
          details: {
            device_count: previewData.length,
            imported_count: successCount,
            file_name: csvFile?.name,
            devices: previewData.map(d => ({
              serial_number: d.serial_number,
              device_name: d.device_name,
              owner_name: d.owner_name
            }))
          },
          success: successCount > 0,
          errorMessage: successCount === 0 ? 'No devices were imported' : undefined
        }
      );
      
      setImportResult({
        success: successCount > 0,
        message: successCount === previewData.length 
          ? `🎉 Successfully imported all ${successCount} devices!`
          : successCount > 0 
            ? `Partially successful: ${successCount} of ${previewData.length} devices imported.`
            : 'Import failed: No devices were imported.',
        count: successCount
      });

      if (successCount > 0) {
        setCompletedSteps(new Set([1, 2, 3, 4]));
      }
    } catch (error) {
      setImportResult({
        success: false,
        message: 'An unexpected error occurred during import.'
      });
    } finally {
      setImporting(false);
    }
  };

  const handleClearAll = () => {
    setCsvFile(null);
    setCsvData('');
    setPreviewData([]);
    setImportResult(null);
    setErrors([]);
    setShowFullPreview(false);
    setCurrentStep(1);
    setCompletedSteps(new Set());
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleStartOver = () => {
    handleClearAll();
    // Smooth scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const steps = [
    { id: 1, title: 'Download Template', description: 'Get the CSV template with correct format', icon: Download, color: 'blue' },
    { id: 2, title: 'Upload File', description: 'Select your CSV file with device data', icon: FileUp, color: 'emerald' },
    { id: 3, title: 'Preview & Import', description: 'Review data and import devices', icon: Eye, color: 'amber' },
    { id: 4, title: 'Complete', description: 'Import finished successfully', icon: CheckCircle, color: 'green' }
  ];

  const getStepStatus = (stepId: number) => {
    if (completedSteps.has(stepId)) return 'completed';
    if (stepId === currentStep) return 'active';
    return 'pending';
  };

  const StepIndicator = ({ step, index }: { step: any; index: number }) => {
    const status = getStepStatus(step.id);
    const isLast = index === steps.length - 1;

    const handleStepClick = () => {
      // Allow navigation to completed steps or current step
      if (completedSteps.has(step.id) || step.id === currentStep) {
        setCurrentStep(step.id);
      }
    };

    return (
      <div className="flex items-center">
        <div className="flex flex-col items-center">
          <div 
            className={`relative w-16 h-16 rounded-3xl flex items-center justify-center shadow-2xl transition-all duration-700 transform cursor-pointer ${
              status === 'completed' 
                ? 'bg-gradient-to-br from-green-500 to-emerald-600 scale-100 rotate-0 hover:scale-105' 
                : status === 'active'
                ? `bg-gradient-to-br from-${step.color}-500 to-${step.color}-600 scale-110 shadow-2xl ${isAnimating ? 'animate-pulse' : ''}`
                : completedSteps.has(step.id) 
                ? 'bg-gray-400 dark:bg-gray-500 scale-95 hover:scale-100'
                : 'bg-gray-300 dark:bg-gray-600 scale-90 opacity-50 cursor-not-allowed'
            }`}
            onClick={handleStepClick}
          >
            {status === 'completed' ? (
              <Check className="w-8 h-8 text-white animate-in zoom-in-50 duration-500" />
            ) : (
              <step.icon className={`w-8 h-8 transition-all duration-500 ${
                status === 'active' ? 'text-white animate-pulse' : 'text-gray-500'
              }`} />
            )}
            {status === 'active' && (
              <div className="absolute inset-0 rounded-3xl bg-white/20 animate-ping"></div>
            )}
          </div>
          <div className={`mt-4 text-center transition-all duration-500 cursor-pointer ${
            status === 'active' ? 'scale-105' : ''
          }`} onClick={handleStepClick}>
            <p className={`text-lg font-bold mb-1 ${
              status === 'active' ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'
            }`}>
              {step.title}
            </p>
            <p className={`text-sm ${
              status === 'active' ? 'text-gray-700 dark:text-gray-300' : 'text-gray-500 dark:text-gray-500'
            }`}>
              {step.description}
            </p>
          </div>
        </div>
        {!isLast && (
          <div className={`w-24 h-2 mx-6 rounded-full transition-all duration-700 ${
            completedSteps.has(step.id) 
              ? 'bg-gradient-to-r from-green-400 to-emerald-500 shadow-lg' 
              : 'bg-gray-300 dark:bg-gray-600'
          }`} />
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8 px-4">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-3/4 w-64 h-64 bg-emerald-400/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
      </div>

      <div className="relative max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 animate-in slide-in-from-top duration-700">
          <div className="inline-flex items-center space-x-4 mb-6">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 via-purple-600 to-blue-600 rounded-3xl flex items-center justify-center shadow-2xl transform rotate-3 hover:rotate-6 transition-transform duration-300">
                <Upload className="w-10 h-10 text-white" />
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center animate-bounce">
                <Sparkles className="w-4 h-4 text-yellow-800" />
              </div>
            </div>
          </div>
          <h1 className="text-5xl font-black text-gray-900 dark:text-white mb-4 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Bulk Import Wizard
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Import multiple devices effortlessly with our guided, step-by-step process. 
            <span className="inline-flex items-center space-x-1 ml-2">
              <Zap className="w-5 h-5 text-yellow-500" />
              <span className="text-yellow-600 dark:text-yellow-400 font-semibold">Fast & Secure</span>
            </span>
          </p>
          
          <div className="flex items-center justify-center space-x-4 mt-6">
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 px-4 py-2 rounded-xl hover:bg-white/60 dark:hover:bg-gray-800/60 transition-all duration-200"
            >
              <Home className="w-5 h-5" />
              <span>Back to Device List</span>
            </button>
            
            {(csvFile || previewData.length > 0 || importResult) && (
              <button
                onClick={handleStartOver}
                className="inline-flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 px-4 py-2 rounded-xl hover:bg-blue-50/60 dark:hover:bg-blue-900/20 transition-all duration-200"
              >
                <RefreshCw className="w-5 h-5" />
                <span>Start Over</span>
              </button>
            )}
          </div>
        </div>

        {/* Progress Steps */}
        <div className="mb-16 animate-in slide-in-from-bottom duration-700 delay-200">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-gray-200/60 dark:border-gray-700/60">
            <div className="flex items-center justify-center">
              {steps.map((step, index) => (
                <StepIndicator key={step.id} step={step} index={index} />
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-8">
          {/* Step 1: Download Template */}
          {currentStep === 1 && (
            <div className={`transform transition-all duration-700 translate-y-0 opacity-100 scale-100 animate-in slide-in-from-bottom`}>
              <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-gray-200/60 dark:border-gray-700/60 hover:shadow-3xl transition-all duration-300">
                <div className="flex items-start space-x-8">
                  <div className="relative">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl flex items-center justify-center shadow-2xl transform hover:scale-110 transition-transform duration-300">
                      <Download className="w-10 h-10 text-white" />
                    </div>
                    {completedSteps.has(1) && (
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center animate-in zoom-in-50 duration-500 shadow-lg">
                        <Check className="w-5 h-5 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-4">
                      <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Download CSV Template</h2>
                      <div className="px-4 py-2 bg-blue-500 text-white rounded-full text-sm font-medium animate-pulse shadow-lg">
                        Start Here
                      </div>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 text-lg mb-8 leading-relaxed">
                      Begin your bulk import journey by downloading our perfectly crafted CSV template. 
                      It includes all the required columns, proper formatting, and example data to guide you through the process.
                    </p>
                    
                    <div className="grid md:grid-cols-2 gap-6 mb-8">
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center space-x-2">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <span>What's Included</span>
                        </h3>
                        <ul className="space-y-3">
                          {[
                            'Required columns: serial_number, device_name, owner_name',
                            'Optional fields: is_locked (true/false), note',
                            'Sample data with 5 example devices',
                            'Proper CSV formatting and structure'
                          ].map((item, index) => (
                            <li key={index} className="flex items-center space-x-3 text-gray-700 dark:text-gray-300 animate-in slide-in-from-left duration-300" style={{ animationDelay: `${index * 100}ms` }}>
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center space-x-2">
                          <Zap className="w-5 h-5 text-yellow-500" />
                          <span>Pro Tips</span>
                        </h3>
                        <ul className="space-y-3">
                          {[
                            'Unlock codes are auto-generated server side',
                            'Use TRUE/FALSE for is_locked column',
                            'Ensure serial numbers are exact and unique',
                            'Notes can include any additional device information'
                          ].map((item, index) => (
                            <li key={index} className="flex items-center space-x-3 text-gray-700 dark:text-gray-300 animate-in slide-in-from-right duration-300" style={{ animationDelay: `${index * 100}ms` }}>
                              <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <button
                      onClick={downloadTemplate}
                      className="group inline-flex items-center space-x-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-10 py-5 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 active:scale-95"
                    >
                      <Download className="w-6 h-6 group-hover:animate-bounce" />
                      <span>Download Template</span>
                      <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform duration-300" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Upload File */}
          {currentStep === 2 && (
            <div className={`transform transition-all duration-700 animate-in slide-in-from-bottom`}>
              <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-gray-200/60 dark:border-gray-700/60 hover:shadow-3xl transition-all duration-300">
                <div className="flex items-start space-x-8">
                  <div className="relative">
                    <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl flex items-center justify-center shadow-2xl transform hover:scale-110 transition-transform duration-300">
                      <FileUp className="w-10 h-10 text-white" />
                    </div>
                    {completedSteps.has(2) && (
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center animate-in zoom-in-50 duration-500 shadow-lg">
                        <Check className="w-5 h-5 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-4">
                      <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Upload Your CSV File</h2>
                      <div className="px-4 py-2 bg-emerald-500 text-white rounded-full text-sm font-medium animate-pulse shadow-lg">
                        Current Step
                      </div>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 text-lg mb-8 leading-relaxed">
                      {csvFile 
                        ? '🎉 Perfect! Your CSV file has been uploaded successfully and is ready for preview.' 
                        : 'Select your prepared CSV file containing all the device information you want to import into the system.'
                      }
                    </p>
                    
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,text/csv"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    
                    <div className="space-y-6">
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={importing}
                          className="group inline-flex items-center space-x-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-10 py-5 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FileUp className="w-6 h-6 group-hover:animate-bounce" />
                          <span>{csvFile ? 'Change CSV File' : 'Select CSV File'}</span>
                          <ChevronRight className="w-6 h-6 group-hover:translate-x-2 transition-transform duration-300" />
                        </button>
                        
                        {csvFile && (
                          <button
                            onClick={handleClearAll}
                            className="group inline-flex items-center space-x-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 px-6 py-3 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200"
                          >
                            <Trash2 className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
                            <span>Clear All</span>
                          </button>
                        )}
                      </div>
                      
                      {csvFile && (
                        <div className="animate-in slide-in-from-top duration-700 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-2xl p-6 border-2 border-emerald-200/60 dark:border-emerald-700/60 shadow-xl">
                          <div className="flex items-center space-x-6">
                            <div className="relative">
                              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-2xl">
                                <FileText className="w-8 h-8 text-white" />
                              </div>
                              <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-400 rounded-full flex items-center justify-center animate-pulse">
                                <Check className="w-4 h-4 text-green-800" />
                              </div>
                            </div>
                            <div className="flex-1">
                              <h4 className="font-bold text-gray-900 dark:text-white text-xl mb-2">{csvFile.name}</h4>
                              <div className="flex items-center space-x-6 text-sm text-gray-600 dark:text-gray-400">
                                <div className="flex items-center space-x-2">
                                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                  <span>{(csvFile.size / 1024).toFixed(1)} KB</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                  <span>CSV Format</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                                  <span className="text-emerald-600 dark:text-emerald-400 font-semibold">Ready for Preview</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Validation Errors */}
          {errors.length > 0 && currentStep === 2 && (
            <div className="animate-in slide-in-from-top duration-500">
              <div className="bg-gradient-to-br from-red-50 via-rose-50 to-pink-50 dark:from-red-900/20 dark:via-rose-900/20 dark:to-pink-900/20 rounded-3xl p-8 border-2 border-red-200/50 dark:border-red-800/50 shadow-2xl">
                <div className="flex items-start space-x-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl flex items-center justify-center shadow-2xl animate-pulse">
                    <AlertCircle className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-red-900 dark:text-red-400 mb-4">Validation Issues Found</h3>
                    <p className="text-red-700 dark:text-red-300 text-lg mb-6">Please fix the following issues before proceeding:</p>
                    <div className="grid gap-3">
                      {errors.map((error, index) => (
                        <div key={index} className="flex items-center space-x-4 text-red-700 dark:text-red-300 animate-in slide-in-from-left duration-300 bg-red-100/50 dark:bg-red-900/30 rounded-xl p-4" style={{ animationDelay: `${index * 100}ms` }}>
                          <div className="w-3 h-3 bg-red-500 rounded-full flex-shrink-0"></div>
                          <span className="font-medium">{error}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Preview & Import */}
          {previewData.length > 0 && errors.length === 0 && currentStep === 3 && (
            <div className="animate-in slide-in-from-bottom duration-700 delay-300">
              <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-gray-200/60 dark:border-gray-700/60 hover:shadow-3xl transition-all duration-300">
                <div className="flex items-start space-x-8 mb-8">
                  <div className="relative">
                    <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-3xl flex items-center justify-center shadow-2xl transform hover:scale-110 transition-transform duration-300">
                      <Eye className="w-10 h-10 text-white" />
                    </div>
                    {completedSteps.has(3) && (
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center animate-in zoom-in-50 duration-500 shadow-lg">
                        <Check className="w-5 h-5 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-4">
                      <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Preview & Import</h2>
                      {currentStep === 3 && (
                        <div className="px-4 py-2 bg-amber-500 text-white rounded-full text-sm font-medium animate-pulse shadow-lg">
                          Almost There!
                        </div>
                      )}
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 text-lg mb-8 leading-relaxed">
                      {previewData.length > 0 
                        ? `🎉 Excellent! We've found ${previewData.length} device${previewData.length === 1 ? '' : 's'} ready to import. Review the data below and click import when you're ready to proceed.`
                        : 'Take a final look at your CSV data before importing to ensure everything looks correct. This step helps prevent any issues during the import process.'
                      }
                    </p>
                    
                    <div className="flex items-center space-x-6">
                      <button
                        onClick={handlePreview}
                        disabled={!csvData.trim() || importing}
                        className="group inline-flex items-center space-x-4 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white px-10 py-5 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Eye className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
                        <span>Preview Data</span>
                        <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform duration-300" />
                      </button>
                      
                      {previewData.length > 0 && (
                        <button
                          onClick={() => setShowFullPreview(!showFullPreview)}
                          className="group inline-flex items-center space-x-3 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 px-6 py-3 rounded-xl transition-all duration-200 font-medium"
                        >
                          <RefreshCw className="w-5 h-5 group-hover:rotate-180 transition-transform duration-300" />
                          <span>{showFullPreview ? 'Show Summary' : 'Show All Rows'}</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Import Button */}
                {previewData.length > 0 && (
                  <div className="animate-in slide-in-from-bottom duration-500 bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-600 rounded-3xl p-8 shadow-2xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-6">
                        <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                          <Zap className="w-8 h-8 text-white animate-pulse" />
                        </div>
                        <div>
                          <h4 className="text-white font-bold text-2xl mb-2">Ready to Import!</h4>
                          <p className="text-white/90 text-lg">{previewData.length} devices will be imported with secure unlock codes</p>
                        </div>
                      </div>
                      <button
                        onClick={handleImport}
                        disabled={importing || previewData.length === 0}
                        className="group inline-flex items-center space-x-4 bg-white text-indigo-600 hover:bg-gray-50 px-10 py-5 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {importing ? (
                          <>
                            <Loader className="w-6 h-6 animate-spin" />
                            <span>Importing...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-6 h-6 group-hover:animate-bounce" />
                            <span>Import {previewData.length} Devices</span>
                            <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform duration-300" />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Preview Table */}
          {previewData.length > 0 && currentStep === 3 && (
            <div className="animate-in slide-in-from-bottom duration-700">
              <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-gray-200/60 dark:border-gray-700/60">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center space-x-4">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                      Data Preview
                    </h3>
                    <div className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full text-sm font-bold shadow-lg">
                      {previewData.length} devices
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-400">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-lg"></div>
                    <span className="font-medium">All data validated ✓</span>
                  </div>
                </div>

                <div className="overflow-hidden rounded-2xl border-2 border-gray-200/60 dark:border-gray-700/60 shadow-2xl">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-gray-50 via-blue-50 to-purple-50 dark:from-gray-700/80 dark:via-gray-600/80 dark:to-gray-700/80">
                      <tr>
                        <th className="text-left py-4 px-6 font-bold text-gray-800 dark:text-gray-200">Serial Number</th>
                        <th className="text-left py-4 px-6 font-bold text-gray-800 dark:text-gray-200">Device Name</th>
                        <th className="text-left py-4 px-6 font-bold text-gray-800 dark:text-gray-200">Owner</th>
                        <th className="text-left py-4 px-6 font-bold text-gray-800 dark:text-gray-200">Status</th>
                        <th className="text-left py-4 px-6 font-bold text-gray-800 dark:text-gray-200">Note</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200/60 dark:divide-gray-600/60">
                      {(showFullPreview ? previewData : previewData.slice(0, 5)).map((device, index) => (
                        <tr 
                          key={index} 
                          className="hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-purple-50/50 dark:hover:from-gray-700/50 dark:hover:to-gray-600/50 transition-all duration-200 animate-in slide-in-from-left" 
                          style={{ animationDelay: `${index * 50}ms` }}
                        >
                          <td className="py-4 px-6 font-mono font-bold text-gray-900 dark:text-white bg-gray-50/50 dark:bg-gray-800/50">{device.serial_number}</td>
                          <td className="py-4 px-6 text-gray-900 dark:text-white font-medium">{device.device_name || '-'}</td>
                          <td className="py-4 px-6 text-gray-900 dark:text-white">{device.owner_name || '-'}</td>
                          <td className="py-4 px-6">
                            <span className={`px-3 py-2 rounded-xl text-sm font-bold shadow-lg ${
                              device.is_locked 
                                ? 'bg-gradient-to-r from-red-100 to-rose-100 dark:from-red-900/30 dark:to-rose-900/30 text-red-700 dark:text-red-400 border-2 border-red-200 dark:border-red-800' 
                                : 'bg-gradient-to-r from-emerald-100 to-green-100 dark:from-emerald-900/30 dark:to-green-900/30 text-emerald-700 dark:text-emerald-400 border-2 border-emerald-200 dark:border-emerald-800'
                            }`}>
                              {device.is_locked ? '🔒 Locked' : '🔓 Unlocked'}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-gray-700 dark:text-gray-300 max-w-xs truncate">{device.note || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!showFullPreview && previewData.length > 5 && (
                    <div className="p-6 text-center bg-gradient-to-r from-gray-50/50 to-blue-50/50 dark:from-gray-800/50 dark:to-gray-700/50 border-t border-gray-200/60 dark:border-gray-700/60">
                      <p className="text-gray-600 dark:text-gray-400 font-medium">
                        ... and {previewData.length - 5} more devices ready to import
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Success/Error Step */}
          {importResult && importResult.count !== undefined && (
            <div className={`transform transition-all duration-700 delay-300 animate-in zoom-in-95`}>
              <div className={`rounded-3xl p-8 border-2 shadow-2xl ${
                importResult.success 
                  ? 'bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 dark:from-emerald-900/20 dark:via-green-900/20 dark:to-teal-900/20 border-emerald-300/50 dark:border-emerald-700/50' 
                  : 'bg-gradient-to-br from-red-50 via-rose-50 to-pink-50 dark:from-red-900/20 dark:via-rose-900/20 dark:to-pink-900/20 border-red-300/50 dark:border-red-700/50'
              }`}>
                <div className="flex items-start space-x-8">
                  <div className={`relative w-24 h-24 rounded-3xl flex items-center justify-center shadow-2xl animate-in zoom-in-50 duration-500 ${
                    importResult.success 
                      ? 'bg-gradient-to-br from-emerald-500 to-green-600' 
                      : 'bg-gradient-to-br from-red-500 to-rose-600'
                  }`}>
                    {importResult.success ? (
                      <>
                        <CheckCircle className="w-12 h-12 text-white animate-bounce" />
                        <div className="absolute inset-0 rounded-3xl bg-white/20 animate-ping"></div>
                      </>
                    ) : (
                      <AlertCircle className="w-12 h-12 text-white animate-pulse" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-4xl font-black mb-4 ${
                      importResult.success ? 'text-emerald-900 dark:text-emerald-400' : 'text-red-900 dark:text-red-400'
                    }`}>
                      {importResult.success ? '🎉 Import Complete!' : '❌ Import Failed'}
                    </h3>
                    <p className={`text-xl mb-6 ${
                      importResult.success ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'
                    }`}>
                      {importResult.message}
                    </p>
                    
                    {importResult.success && importResult.count && (
                      <div className="space-y-4 mb-8">
                        {[
                          'All devices imported successfully with unique IDs',
                          'Secure unlock codes generated automatically',
                          'Device list updated in real-time',
                          'Audit logs created for compliance'
                        ].map((item, index) => (
                          <div key={index} className="flex items-center space-x-4 text-emerald-700 dark:text-emerald-400 animate-in slide-in-from-left duration-300" style={{ animationDelay: `${index * 100}ms` }}>
                            <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                              <Check className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-medium text-lg">{item}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-4">
                      {importResult.success && (
                        <button
                          onClick={() => {
                            // Add refresh parameter to trigger device list refresh
                            const url = new URL(window.location.href);
                            url.pathname = '/'; // Go back to main device list
                            url.searchParams.set('refresh', 'true');
                            window.location.href = url.toString();
                          }}
                          className="inline-flex items-center space-x-4 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white px-10 py-5 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 active:scale-95"
                        >
                          <CheckCircle className="w-6 h-6" />
                          <span>Return to Device List</span>
                          <ArrowRight className="w-6 h-6" />
                        </button>
                      )}
                      
                      <button
                        onClick={handleStartOver}
                        className={`inline-flex items-center space-x-3 px-8 py-4 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105 ${
                          importResult.success 
                            ? 'text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/30' 
                            : 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white shadow-xl hover:shadow-2xl'
                        }`}
                      >
                        <RefreshCw className="w-5 h-5" />
                        <span>Start New Import</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
